import {
  BufferGeometry,
  Sphere,
  Vector3,
  Matrix4,
  NormalBlending,
  Mesh,
  LineSegments,
  Points,
  FrontSide,
  BackSide,
} from "three";
import BufferAttr from "./BufferAttribute";

class BaseMesh {
  constructor({
    uniforms,
    conf,
    verticesPerItem,
    idxPerItem,
    idxPattern,
    idxOffset,
    isPoints,
    isLineSeq,
  }) {
    this.maxCount = 0;
    this.attrs = {};
    this.conf = conf || {};
    this.verticesPerItem = verticesPerItem;
    this.idxOffset = idxOffset || 0;
    this.idxPerItem = idxPerItem || 1;
    this.idxPattern = idxPattern;
    this.multiplier = Math.max(this.idxPerItem, this.verticesPerItem);

    this.uniforms = uniforms;
    this.geom = new BufferGeometry();
    this.createDefaultBuffers();
    this.isPoints = Boolean(isPoints);
    this.isLineSeq = Boolean(isLineSeq);
    this.attrKeys = Object.keys(this.attrs);
    this.material = this.uniforms.compileMaterial(this.isPoints);

    this.createMesh();
    this.items = [];
    this.isVisible = true;
    this.height = 1;
    this.width = 1;
    this.pxRatio = 1;
    this.isInverted = false;
    this.blending = null;
    this.uRes = 1;

    // scale
    this.scaleY = 1;
    this.transformY = 0;
    this.scaleZ = 0;

    // default buffers
    this.geom.setDrawRange(0, 0);
    this.resizeListener = null;
    this.raycastOffset = 0;
    this.name = "";
  }

  setName(name) {
    this.name = name;
  }

  addResizeListener(callback) {
    this.resizeListener = callback;
  }

  setRaycastOffset(offset) {
    this.raycastOffset = offset;
    this.uniforms.setRaycastOffset(offset);
    return this.items.length + offset;
  }

  setWidth(width) {
    if (width === this.width) return;
    this.width = width;
    this.recalcDimensions();
  }

  setHeight(height) {
    if (height === this.height) return;
    this.height = height;
    this.recalcDimensions();
  }

  setPxRatio(pxRatio) {
    if (pxRatio === this.pxRatio) return;
    this.pxRatio = pxRatio;
    this.recalcDimensions();
  }

  setIsInverted(isInverted) {
    if (isInverted === this.isInverted) return;
    this.isInverted = isInverted;
    this.recalcDimensions();
  }

  setXStart(start) {
    this.material.uniforms.u_x_start.value = start;
  }

  setXDist(dist) {
    this.material.uniforms.u_x_dist.value = dist;
  }

  recalcDimensions() {
    const invertMult = this.isInverted ? -1 : 1;
    const uWidth = this.width * invertMult;
    const uHeight = this.height;
    const uRes = this.pxRatio;
    const side = this.isInverted ? BackSide : FrontSide;

    this.material.side = side;
    this.material.uniforms.u_width.value = uWidth;
    this.material.uniforms.u_height.value = uHeight;
    this.material.uniforms.u_resolution.value = uRes;
  }

  createDefaultBuffers() {
    this.addBuffer(1, this.verticesPerItem, "raycastInd", Float32Array);

    if (this.conf.posX) {
      this.addBuffer(2, this.verticesPerItem, "posX", Float32Array);
    }
    if (this.conf.posY) {
      this.addBuffer(1, this.verticesPerItem, "posY", Float32Array);
    }
    if (this.conf.rgbaCode) {
      this.addBuffer(1, this.verticesPerItem, "rgbaCode", Uint8Array);
    }
    if (this.conf.offset) {
      this.addBuffer(2, this.verticesPerItem, "offset", Float32Array);
    }
    if (this.conf.textureCoordsInd) {
      this.addBuffer(1, this.verticesPerItem, "textureCoordsInd", Uint8Array);
    }
    if (this.conf.posYInd) {
      this.addBuffer(1, this.verticesPerItem, "posYInd", Uint16Array);
    }
    if (!this.isPoints) {
      this.addBuffer(1, this.verticesPerItem, "vInd", Uint8Array);
    }
    if (this.idxPattern) {
      this.addBuffer(1, this.idxPerItem, "index", Uint32Array);
    }
  }

  setScene(scene) {
    if (this.scene) {
      this.scene.remove(this.mesh);
    }
    this.scene = scene;
    this.scene.attach(this.mesh);
    this.scene.add(this.mesh);
  }

  createMesh() {
    if (this.isPoints) {
      this.mesh = new Points(this.geom, this.material);
    } else if (this.isLineSeq) {
      this.mesh = new LineSegments(this.geom, this.material);
    } else {
      this.mesh = new Mesh(this.geom, this.material);
    }
  }

  clone() {
    if (!this.scene) return;
    const previousAttrs = { ...this.attrs };
    const previousMesh = this.mesh;
    const prevUniforms = previousMesh.material.uniforms;

    this.geom = new BufferGeometry();
    this.material = this.uniforms.compileMaterial(this.isPoints);
    this.material.side = this.isInverted ? BackSide : FrontSide;
    this.material.blending = previousMesh.material.blending;
    this.material.blendEquation = previousMesh.material.blendEquation;
    this.material.blendSrc = previousMesh.material.blendSrc;
    this.material.blendDst = previousMesh.material.blendDst;

    this.createMesh();
    this.mesh.visible = previousMesh.visible;

    this.createDefaultBuffers();
    this.copyBuffersFrom(previousAttrs);

    previousMesh.material.dispose();
    previousMesh.geometry.dispose();
    if (previousMesh.material.texture) {
      previousMesh.material.texture.dispose();
    }
    if (previousMesh.material.uniforms?.u_texture?.value) {
      previousMesh.material.uniforms.u_texture.value.dispose();
    }
    this.scene.remove(previousMesh);
    this.mesh.material.uniforms = prevUniforms;
    this.setScene(this.scene);
    this.updateBoundingSphere();
  }

  dispose() {
    this.mesh.material.dispose();
    if (this.mesh.material.texture) {
      this.mesh.material.texture.dispose();
    }
    if (this.mesh.material.uniforms?.value?.u_texture) {
      this.mesh.material.uniforms.u_texture.value.dispose();
    }
    this.mesh.geometry.dispose();
    this.scene.remove(this.mesh);
    this.items = [];
  }

  autoFillDefaultBuffers(newCount) {
    const { index, raycastInd, vInd } = this.attrs;
    const idxOff = this.idxOffset;
    const indexVec = this.idxPattern;
    const vIndVec = new Float32Array(this.verticesPerItem).map((_, i) => i);
    const indVec = new Float32Array(this.verticesPerItem);

    if (index) index.fillWithPattern(newCount, indexVec, idxOff);
    if (vInd) vInd.fillWithVec(newCount, vIndVec);
    if (raycastInd) raycastInd.fillWithPattern(newCount, indVec);
  }

  setItemLen(len) {
    if (len > this.maxCount) {
      this.maxCount = len;
      this.clone();
    }
    this.autoFillDefaultBuffers(len);
    this.geom.setDrawRange(0, len * this.multiplier);

    if (this.resizeListener && typeof this.resizeListener === "function") {
      this.resizeListener(this);
    }
  }

  setItems(items) {
    this.items = items;
    this.setItemLen(this.items.length);
  }

  reset() {
    this.items = [];
    this.maxCount = 0;
    this.geom.setDrawRange(0, 0);

    this.createDefaultBuffers();

    if (this.resizeListener && typeof this.resizeListener === "function") {
      this.resizeListener(this);
    }
  }

  setBlending(blending) {
    this.blending = blending;
    this.mesh.material.blending = blending;
  }

  startRaycasting(scene, shrinkFactor = 1) {
    this.originalScene = this.scene;
    this.setScene(scene);
    this.blending = this.mesh.material.blending;
    this.uRes = this.mesh.material.uniforms.u_resolution.value;
    this.mesh.material.uniforms.u_resolution.value = 1 / shrinkFactor;
    this.mesh.material.blending = NormalBlending;
    this.onStartRaycast();
    if (!this.canRaycast()) this.hide();
    return this;
  }

  stopRaycasting() {
    this.setScene(this.originalScene);
    this.mesh.material.blending = this.blending;
    this.mesh.material.uniforms.u_resolution.value = this.uRes;
    this.revertVisibility();
    this.onEndRaycast();
  }

  getItemOnIndex(index) {
    if (index === null) return null;
    const ind = index - this.raycastOffset;
    if (ind >= 0 && this.items[ind]) {
      return { mesh: this, item: this.items[ind], index: ind };
    }
    return null;
  }

  revertVisibility() {
    this.mesh.visible = this.isVisible;
  }

  setIsVisible(val) {
    this.isVisible = val;
    this.mesh.visible = this.isVisible;
  }

  hide() {
    this.mesh.visible = false;
    return this;
  }

  show() {
    this.mesh.visible = true;
  }

  canRaycast() {
    return !this.uniforms.disableRaycast;
  }

  copyBuffersFrom(buffers) {
    Object.values(buffers).forEach((attr) => {
      this.attrs[attr.name].copyFrom(attr.buffer);
      this.attrs[attr.name].updateItemCount(attr.itemCount, true);
    });
  }

  addBuffer(size, v, name, type) {
    this.attrs[name] = new BufferAttr(name, this.maxCount, size, v, type);
    if (name === "index") {
      this.geom.setIndex(this.attrs.index.buffer);
    } else {
      this.geom.setAttribute(name, this.attrs[name].buffer);
    }
  }

  updateBoundingSphere() {
    const sphereCenter = new Vector3(0.5, 0.5, 0);
    const sphereRadius = this.scaleY / 2;
    const matrix = new Matrix4().set(
      ...[1, 0, 0, 0], //
      ...[0, this.scaleY, 0, this.transformY], //
      ...[0, 0, 1, 0], //
      ...[0, 0, 0, 1] //
    );
    this.mesh.matrix = matrix;
    this.mesh.matrixAutoUpdate = false;
    this.mesh.renderOrder = this.scaleZ;
    this.geom.boundingSphere = new Sphere(sphereCenter, sphereRadius);
  }

  setBoundingSphere(scaleY, transformY, scaleZ) {
    this.scaleY = scaleY;
    this.transformY = transformY;
    this.scaleZ = scaleZ;
    this.updateBoundingSphere();
  }

  // to extend
  onStartRaycast() {
    return this;
  }

  // to extend
  onEndRaycast() {
    return this;
  }
}

export default BaseMesh;
